import type { DoseStatus } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import type { MedicationsToday } from '../lib/api.js';
import { DoseSlotCard, logSlotOptimistic } from './DoseSlots.js';

/**
 * One past day's doses, correctable.
 *
 * The counterpart to the carry-over on Today, which only reaches back as far as
 * last night and only until 4am. This is for noticing on Thursday that Monday
 * was wrong — and for the ordinary case of having tapped the wrong thing, which
 * nothing in the app could fix once the day had turned.
 *
 * Every status stays editable, including ones already answered. A dose recorded
 * as taken that was actually missed is the correction most worth making and the
 * one a "log it once" design quietly forbids.
 */
export function DayDoses({ day, active }: { day: string; active: boolean }): JSX.Element | null {
  const [data, setData] = useState<MedicationsToday | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!active || data || failed) return;
    let cancelled = false;
    api
      .medicationsOnDay(day)
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [active, day, data, failed]);

  if (!active || failed) return null;
  if (!data) return null;
  if (data.slots.length === 0) return null;

  return (
    <div className="stack-sm stack">
      <h4 className="faint" style={{ margin: 0 }}>
        Doses
      </h4>
      {/*
        THREE, NOT TWO. A rail for two doses scrolled where it did not need to.
        
        The rail exists so a card stays wide enough for its three chips to sit on
        one line — narrower and they wrap, costing a row of height on every card
        at once. A STACK does not have that problem at all: stacked cards are
        full width, so the chips are never in danger.
        
        So with two doses the rail was buying nothing and charging for it. On a
        phone you saw one card and had to swipe; on a desktop, where this app is
        still a ~544px column, two 19rem cards plus padding need 648px and the
        second was sliced down the middle of a word with the scrollbar hidden.
        Its owner read that as broken, which is a fair reading of a cut word.
        
        Two cards stacked cost about 140px of height and clip nothing. From
        three, that trade flips and the rail earns its keep again.
      */}
      <div className={data.slots.length > 2 ? 'dose-rail' : 'stack-sm stack'}>
        {data.slots.map((slot) => (
          <DoseSlotCard
            key={`${slot.medicationId}-${slot.scheduledFor}`}
            slot={slot}
            // The day is over, so nothing here is "still to come" and the clock
            // has nothing left to say about it.
            clockApplies={false}
            nowClock="24:00"
            compact={data.slots.length > 1}
            day={day}
            onLog={(s, status: DoseStatus) => {
              void logSlotOptimistic(data, s, status, setData, setError);
            }}
            onTimeChanged={() => {
              void api.medicationsOnDay(day).then(setData).catch(() => {});
            }}
          />
        ))}
      </div>
      {error && <p className="faint">{error}</p>}
    </div>
  );
}
